/** @file Hook: pushes a shortcut context while focus is inside an element, pops it when focus leaves. */
import { pushContext } from '@pose2d/shortcuts/pushContext';
import { useEffect, type RefObject } from 'react';

/** The context's chords (registered with registerEffective) fire only while the element holds focus. */
export function useFocusContext(ref: RefObject<HTMLElement | null>, context: string): void {
  useEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    let pop: (() => void) | null = null;
    const enter = (): void => {
      pop ??= pushContext(context);
    };
    const leave = (e: FocusEvent): void => {
      if (e.relatedTarget instanceof Node && el.contains(e.relatedTarget)) return;
      pop?.();
      pop = null;
    };
    el.addEventListener('focusin', enter);
    el.addEventListener('focusout', leave);
    return () => {
      el.removeEventListener('focusin', enter);
      el.removeEventListener('focusout', leave);
      pop?.();
    };
  }, [ref, context]);
}
